/**
 * Orders Page - Refactored with DataGrid, PageHeader and Selection
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { ClipboardList, Eye, Trash2, Table2, Users, Download, FileText, FileSpreadsheet, ChevronDown, CheckSquare, Clock, CheckCircle, XCircle } from 'lucide-react';

import OrderDetailModal from './OrderDetailModal';
import './styles.css';

const statusLabels = {
  open: 'Aberta',
  partial: 'Parcial',
  closed: 'Fechada',
  cancelled: 'Cancelada',
};

const statusVariants = {
  open: 'default',
  partial: 'warning',
  closed: 'secondary',
  cancelled: 'destructive',
};

export default function Orders() {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useInstance();
  const gridRef = useRef(null);
  
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Server-side pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({});

  // Selection state
  const [selectedCount, setSelectedCount] = useState(0);

  // Quick filter state
  const [quickFilter, setQuickFilter] = useState(null);

  // Load orders with server-side params
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters object
      const apiFilters = {};
      if (filters.status && filters.status !== 'all') apiFilters.status = filters.status;
      if (filters.table && filters.table !== 'all') apiFilters.table_id = filters.table;

      // Apply quick filter
      if (quickFilter === 'open') apiFilters.status = 'open';
      if (quickFilter === 'partial') apiFilters.status = 'partial';
      if (quickFilter === 'closed') apiFilters.status = 'closed';

      const response = await api.post('/orders/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
      });

      if (response.data?.success) {
        setOrders(response.data.data?.items || []);
        setTotalItems(response.data.data?.total || 0);
      } else {
        setOrders(response.data?.data?.items || response.data || []);
        setTotalItems(response.data?.data?.total || response.data?.length || 0);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
      // Fallback to old endpoint
      try {
        const res = await api.get('/orders');
        setOrders(res.data || []);
        setTotalItems(res.data?.length || 0);
      } catch (e) {
        toast.error(t('errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters, quickFilter, t]);

  // Load tables and customers
  const loadAuxData = useCallback(async () => {
    try {
      const [tablesRes, customersRes] = await Promise.all([
        api.get('/tables'),
        api.get('/customers'),
      ]);
      setTables(tablesRes.data || []);
      setCustomers(customersRes.data || []);
    } catch (err) {
      console.error('Error loading aux data:', err);
    }
  }, []);

  useEffect(() => {
    loadAuxData();
  }, [loadAuxData]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Handle page change from grid
  const handlePageChange = useCallback((newPage, newPageSize) => {
    setPageNumber(newPage);
    setPageSize(newPageSize);
  }, []);

  // Handle sort change from grid
  const handleSortChange = useCallback((newSortBy, newSortDirection) => {
    if (newSortBy && newSortDirection) {
      setSortBy(newSortBy);
      setSortDirection(newSortDirection);
    }
  }, []);

  // Handle filter search
  const handleSearch = useCallback(() => {
    setPageNumber(1);
    loadOrders();
  }, [loadOrders]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setQuickFilter(null);
    setPageNumber(1);
  }, []);

  // Quick filter handler
  const handleQuickFilter = useCallback((filter) => {
    setQuickFilter(prev => prev === filter ? null : filter);
    setPageNumber(1);
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/orders/${deleteId}`);
      toast.success(t('orders.deleteSuccess'));
      setDeleteId(null);
      loadOrders();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  const getTableNumber = (id) => tables.find(t => t.id === id)?.number || '-';
  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || '-';

  // Export handlers
  const handleExportCSV = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    gridRef.current?.exportCSV(onlySelected);
  }, [selectedCount]);

  const handleExportJSON = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    const data = gridRef.current?.exportJSON(onlySelected);
    if (!data?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comandas_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedCount]);

  // Track selection changes
  const handleSelectionChanged = useCallback(() => {
    const count = gridRef.current?.getSelectedCount() || 0;
    setSelectedCount(count);
  }, []);

  const filterDefinitions = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'open', label: 'Aberta' },
        { value: 'partial', label: 'Parcial' },
        { value: 'closed', label: 'Fechada' },
        { value: 'cancelled', label: 'Cancelada' },
      ],
    },
    {
      key: 'table',
      label: 'Mesa',
      type: 'select',
      placeholder: 'Todas',
      options: [
        { value: 'all', label: 'Todas' },
        ...tables.map(t => ({ value: t.id, label: `Mesa ${t.number}` })),
      ],
    },
  ], [tables]);

  const columnDefs = useMemo(() => [
    {
      field: 'id',
      headerName: t('orders.orderNumber'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-muted-foreground" />
          <span className="font-mono text-sm">#{params.value?.slice(-6)}</span>
        </div>
      ),
    },
    {
      field: 'table_id',
      headerName: t('orders.table'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        params.value ? (
          <div className="flex items-center gap-1">
            <Table2 className="h-4 w-4" />
            Mesa {getTableNumber(params.value)}
          </div>
        ) : (
          <span className="text-muted-foreground">Balcão</span>
        )
      ),
    },
    {
      field: 'customer_id',
      headerName: t('orders.customer'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        params.value ? (
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            {getCustomerName(params.value)}
          </div>
        ) : '-'
      ),
    },
    {
      field: 'items',
      headerName: t('orders.items'),
      flex: 0.5,
      sortable: false,
      valueGetter: (params) => params.data.items?.length || 0,
    },
    {
      field: 'total',
      headerName: t('orders.total'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        <span className="font-mono font-semibold">
          {formatCurrency(params.value)}
        </span>
      ),
    },
    {
      field: 'status',
      headerName: t('orders.status'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        <Badge variant={statusVariants[params.value] || 'default'}>
          {statusLabels[params.value] || params.value}
        </Badge>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Data',
      flex: 1,
      sortable: true,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      flex: 0.8,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="datagrid-actions">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedOrder(params.data)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {params.data.status === 'open' && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDeleteId(params.data.id)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ], [t, formatCurrency, formatDate, tables, customers]);

  // Quick filters
  const quickFilters = [
    { key: 'open', label: 'Abertas', icon: Clock, color: 'emerald' },
    { key: 'partial', label: 'Parciais', icon: Clock, color: 'amber' },
    { key: 'closed', label: 'Fechadas', icon: CheckCircle, color: 'gray' },
  ];

  // Export actions dropdown
  const ExportActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
          {selectedCount > 0 && (
            <Badge variant="secondary" className="ml-2">{selectedCount}</Badge>
          )}
          <ChevronDown className="h-3 w-3 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExportCSV(false)}>
          <FileText className="h-4 w-4 mr-2" />
          Exportar Tudo (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportJSON(false)}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Exportar Tudo (JSON)
        </DropdownMenuItem>
        {selectedCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportCSV(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportJSON(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (JSON)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="orders-page" data-testid="orders-page">
      <PageHeader
        title={t('orders.title')}
        icon={ClipboardList}
        subtitle={`${totalItems} comandas`}
        actions={<ExportActions />}
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        showExport={false}
      />

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {quickFilters.map(qf => {
          const Icon = qf.icon;
          const isActive = quickFilter === qf.key;
          return (
            <Button
              key={qf.key}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter(qf.key)}
              data-testid={`quick-filter-${qf.key}`}
            >
              <Icon className="h-4 w-4 mr-1" />
              {qf.label}
            </Button>
          );
        })}
        {quickFilter && (
          <Button variant="ghost" size="sm" onClick={() => setQuickFilter(null)}>
            Limpar filtro
          </Button>
        )}
      </div>

      <DataGrid
        ref={gridRef}
        title="Lista de Comandas"
        columnDefs={columnDefs}
        rowData={orders}
        loading={loading}
        enableSelection={true}
        getRowId={(params) => params.data.id}
        serverSide={true}
        totalItems={totalItems}
        pageNumber={pageNumber}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortDirection={sortDirection}
        paginationPageSize={pageSize}
        onSelectionChanged={handleSelectionChanged}
      />

      <OrderDetailModal
        open={!!selectedOrder}
        onOpenChange={() => setSelectedOrder(null)}
        order={selectedOrder}
        getTableNumber={getTableNumber}
        getCustomerName={getCustomerName}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('orders.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
